import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { Splash } from '@/components/layout/Splash';

interface AuthGuardProps {
    children: React.ReactNode;
}

/**
 * AuthGuard Component
 *
 * Protects routes that require authentication.
 * Redirects to login if user is not authenticated.
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/protected"
 *   element={
 *     <AuthGuard>
 *       <ProtectedPage />
 *     </AuthGuard>
 *   }
 * />
 * ```
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const { isAuthenticated, user, isInitialized } = useAppSelector((state) => state.auth);

    if (!isInitialized) {
        return <Splash />;
    }

    if (!isAuthenticated || !user) {
        // Redirect to login page
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

export default AuthGuard;